import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import RecordModal from "../components/RecordModal";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import {
  Loader2, Mic, Keyboard, Heart, Check, ImagePlus, X, Send, Sparkles,
} from "lucide-react";

export default function Contribute() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState(null); // {id, text} or null (= something else)
  const [mode, setMode] = useState("type");
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [audioId, setAudioId] = useState(null);
  const [photo, setPhoto] = useState(null); // { file_id, previewUrl, name }
  const [photoBusy, setPhotoBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/contribute/${token}`)
      .then(({ data }) => { setInfo(data); })
      .catch((e) => setLoadErr(formatApiError(e?.response?.data?.detail) || "This link is no longer active."))
      .finally(() => setLoading(false));
  }, [token]);

  const onTranscript = (text, aId) => {
    setTranscript((prev) => (prev ? prev + " " : "") + text);
    setAudioId(aId);
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/contribute/${token}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhoto({ file_id: data.file_id, previewUrl: URL.createObjectURL(file), name: file.name });
    } catch { toast.error("Couldn't upload that photo. Please try another."); }
    finally { setPhotoBusy(false); }
  };

  const deriveTitle = (text) => {
    const words = text.trim().split(/\s+/);
    const t = words.slice(0, 9).join(" ");
    return (words.length > 9 ? t + "…" : t) || "A shared memory";
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Please add your name so they know who shared it."); return; }
    if (!transcript.trim()) { toast.error("Please share a memory before sending."); return; }
    setSubmitting(true);
    try {
      await api.post(`/contribute/${token}`, {
        contributor_name: name.trim(),
        question_id: prompt?.id || null,
        question_text: prompt?.text || deriveTitle(transcript),
        original_transcript: transcript.trim(),
        source: audioId ? "voice" : "typed",
        audio_id: audioId,
        photo_file_id: photo?.file_id || null,
        photo_caption: "",
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "We couldn't send that. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen grain bg-cream flex items-center justify-center"><Loader2 className="animate-spin text-teal" size={30} /></div>;
  }

  if (loadErr) {
    return (
      <div className="min-h-screen grain bg-cream flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl border border-line p-10 text-center">
          <X className="mx-auto text-subtle" size={44} />
          <h1 className="font-serif text-2xl text-charcoal mt-5">Link not active</h1>
          <p className="text-subtle mt-2">{loadErr}</p>
        </div>
      </div>
    );
  }

  const storyteller = info?.storyteller_name || "your family member";

  if (done) {
    return (
      <div className="min-h-screen grain bg-cream flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl border border-line p-10 text-center animate-fade-up" data-testid="contribute-done">
          <div className="w-20 h-20 rounded-full bg-sagelight flex items-center justify-center mx-auto text-teal"><Check size={40} /></div>
          <h1 className="font-serif text-3xl text-charcoal mt-6">Thank you</h1>
          <p className="text-subtle mt-3">Your memory has been sent to {storyteller}. They'll see it and decide whether to add it to their story.</p>
          <button data-testid="contribute-another-btn"
            onClick={() => { setDone(false); setPrompt(null); setTranscript(""); setAudioId(null); setPhoto(null); }}
            className="mt-8 inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-7 py-3 rounded-full font-semibold">
            <Sparkles size={17} /> Share another memory
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-cream" data-testid="contribute-page">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2.5">
          <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
          <Wordmark size="text-lg sm:text-xl" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 relative z-10">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold">
            <Heart size={14} /> A memory for {storyteller}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mt-3 leading-tight">
            Share a memory of {storyteller}
          </h1>
          <p className="text-subtle mt-3 leading-relaxed">
            {storyteller} is writing their life story. Add a memory of your own — a moment together, something
            they taught you, a story that makes you smile. They'll read it and decide whether to include it.
          </p>
        </div>

        {/* Name */}
        <div className="mt-8">
          <label className="text-sm font-semibold text-charcoal">Your name</label>
          <input data-testid="contribute-name-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah (their daughter)"
            className="mt-2 w-full h-12 px-4 rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none" />
        </div>

        {/* Prompt chooser */}
        <div className="mt-6">
          <label className="text-sm font-semibold text-charcoal">Choose a prompt, or just start writing</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {(info?.questions || []).map((q) => (
              <button key={q.id} data-testid={`contribute-prompt-${q.id}`} onClick={() => setPrompt(q)}
                className={`text-left text-sm px-4 py-2 rounded-full border transition-colors ${
                  prompt?.id === q.id ? "bg-teal text-cream border-teal" : "bg-white text-charcoal border-line hover:border-sage"
                }`}>
                {q.text}
              </button>
            ))}
            <button data-testid="contribute-prompt-freeform" onClick={() => setPrompt(null)}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                prompt === null ? "bg-teal text-cream border-teal" : "bg-white text-charcoal border-line hover:border-sage"
              }`}>
              Something else
            </button>
          </div>
        </div>

        {/* Memory input */}
        <div className="mt-6">
          <div className="flex gap-2">
            <button data-testid="contribute-mode-speak" onClick={() => { setMode("speak"); setRecording(true); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "speak" ? "bg-teal text-cream" : "bg-white border border-line text-subtle"}`}>
              <Mic size={16} /> Speak
            </button>
            <button data-testid="contribute-mode-type" onClick={() => setMode("type")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "type" ? "bg-teal text-cream" : "bg-white border border-line text-subtle"}`}>
              <Keyboard size={16} /> Type
            </button>
          </div>

          {mode === "speak" && !transcript && (
            <button data-testid="contribute-start-record" onClick={() => setRecording(true)}
              className="mt-4 w-full flex flex-col items-center gap-3 bg-white border border-line rounded-2xl py-10 hover:border-coral transition-colors">
              <span className="w-16 h-16 rounded-full bg-coral flex items-center justify-center text-white"><Mic size={28} /></span>
              <span className="text-subtle">Tap to start speaking</span>
            </button>
          )}

          <textarea data-testid="contribute-textarea" value={transcript} onChange={(e) => setTranscript(e.target.value)}
            placeholder={mode === "speak" ? "Your words will appear here — you can tidy them up before sending." : "Just start telling the story…"}
            className="mt-4 w-full min-h-[180px] p-5 text-lg leading-relaxed rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none resize-y" />
          {mode === "speak" && transcript && (
            <button data-testid="contribute-add-voice" onClick={() => setRecording(true)} className="mt-3 text-teal text-sm font-medium flex items-center gap-1.5">
              <Mic size={15} /> Add more by voice
            </button>
          )}
        </div>

        {/* Optional photo */}
        <div className="mt-6">
          <label className="text-sm font-semibold text-charcoal">Add a photo (optional)</label>
          {photo ? (
            <div className="mt-3 flex items-center gap-4">
              <img src={photo.previewUrl} alt="Selected" className="h-20 w-20 rounded-xl object-cover border border-line" />
              <button data-testid="contribute-remove-photo" onClick={() => setPhoto(null)} className="inline-flex items-center gap-1.5 text-subtle hover:text-destructive text-sm">
                <X size={15} /> Remove
              </button>
            </div>
          ) : (
            <label data-testid="contribute-photo-label" className="mt-3 inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm cursor-pointer transition-colors">
              {photoBusy ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Choose a photo
              <input data-testid="contribute-photo-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} disabled={photoBusy} />
            </label>
          )}
        </div>

        <button data-testid="contribute-submit-btn" onClick={submit} disabled={submitting}
          className="mt-8 w-full flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white h-14 rounded-full font-semibold text-lg transition-colors disabled:opacity-60">
          {submitting ? <Loader2 className="animate-spin" size={20} /> : <><Send size={19} /> Send this memory to {storyteller}</>}
        </button>
        <p className="text-center text-xs text-subtle mt-4">Your memory is private and only shared with {storyteller}.</p>
      </main>

      <RecordModal open={recording} onClose={() => setRecording(false)} onTranscript={onTranscript}
        endpoint={`/contribute/${token}/transcribe`} />
    </div>
  );
}
